import { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useI18nStore, type LanguageCode } from '@/store/i18nStore';

/**
 * LanguageSelector — Selettore lingua con dropdown.
 *
 * Usa un'icona Globe per evitare il bug Windows dove le flag emoji (es. 🇮🇹)
 * vengono renderizzate come testo (IT) duplicando il codice lingua. Dark mode first.
 */
export default function LanguageSelector() {
  const { language, languages, setLanguage } = useI18nStore();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === language);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium
                   text-surface-600 hover:text-surface-900 hover:bg-surface-50
                   dark:text-surface-400 dark:hover:text-white dark:hover:bg-surface-800
                   transition-colors"
        aria-label="Seleziona lingua"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-5 h-5" />
        <span className="hidden sm:inline">{currentLang?.code.toUpperCase() ?? 'IT'}</span>
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 z-50 min-w-[180px]
                     bg-white dark:bg-[#242428] rounded-xl shadow-lg
                     border border-surface-200 dark:border-surface-700
                     py-1 animate-in fade-in"
          role="listbox"
          aria-label="Lingue disponibili"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === language}
              onClick={() => {
                setLanguage(lang.code as LanguageCode);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left
                         transition-colors hover:bg-surface-50 dark:hover:bg-surface-700/50
                         ${lang.code === language
                           ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-500/10 font-medium'
                           : 'text-surface-700 dark:text-surface-300'
                         }`}
            >
              <span className="text-xs font-bold uppercase tracking-wide w-6 text-center">{lang.code}</span>
              <span className="flex-1">{lang.native}</span>
              {lang.code === language && (
                <svg className="w-4 h-4 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
